import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import {
  faComment,
  faMobileScreen,
  faEnvelope,
  faLocationDot,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Button } from 'react-bootstrap';
import Link from 'components/Link';
import {
  setCurrentModal,
  MODAL_SELECT_STORE,
} from 'stores/reducers/store-selection-modal';
import cleanObject from 'utils/clean-object';
import styles from './NoItemsFoundContent.module.scss';

const NoItemsFoundContent = ({
  facets = [],
  filterParam,
  noItemsContent,
  optionsType,
  path,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const sanitizedParams = filterParam?.split(/,|%2C/).filter(Boolean);
  const hasFilters = sanitizedParams?.length;
  const visibleFacets = facets?.filter((_) => _.visible).map((_) => _.facet);
  const hiddenFilterParams = sanitizedParams?.filter(
    (sanitizedParam) => !visibleFacets.some((vf) => sanitizedParam.includes(vf))
  );

  if (!noItemsContent) return null;

  const {
    message: noItemsFoundMessage,
    contact_info: {
      change_location: changeLocation,
      email,
      header: contactHeader,
      phone,
      sms,
    },
    shopping_options: shoppingOptions,
  } = noItemsContent || {};

  const filteredShoppingOptions = shoppingOptions?.find(
    (option) => option.type?.toLowerCase() === optionsType?.toLowerCase()
  );

  return (
    <div className="text-primary">
      <div className="mb-15 mb-md-30 mx-8">
        <div
          className={classNames('mb-15', {
            'mb-md-30 mt-14 mt-md-22': !hasFilters,
            'mb-xxl-20 mt-12 mt-md-0': hasFilters,
          })}
        >
          <h2
            className={classNames({
              'fs-16 fs-md-18 fs-lg-20': !hasFilters,
              'fs-16 fs-md-18': hasFilters,
            })}
          >
            {noItemsFoundMessage}
          </h2>
          {hasFilters ? null : (
            <div className="mt-15">
              <Link
                href="/shop/all"
                className={classNames(
                  'btn',
                  'btn-secondary',
                  styles.shop_all_button,
                  'rounded-0',
                  'fs-7',
                  'align-self-end',
                  'border-2',
                  'text-uppercase',
                  'py-5'
                )}
              >
                Shop All
              </Link>
            </div>
          )}
        </div>
        {hasFilters ? (
          <p className="mb-15 mb-xxl-20">
            You can adjust or{' '}
            <Link
              shallow
              href={{
                pathname: path,
                query: cleanObject({
                  sort: router.query.sort,
                  search: router.query.search,
                  ...(hiddenFilterParams && {
                    filters: hiddenFilterParams.join(','),
                  }),
                }),
              }}
              className="text-decoration-hover-none text-decoration-underline"
            >
              clear your filters
            </Link>
            {' or '}
            <button
              data-value="no-facets-select-store-link"
              type="button"
              className="bg-transparent border-0 p-0 text-decoration-hover-none text-decoration-underline text-primary"
              onClick={() => dispatch(setCurrentModal(MODAL_SELECT_STORE))}
            >
              try another store.
            </button>
          </p>
        ) : null}
        <div className="row">
          {hasFilters ? null : (
            <div className="col-12 col-sm mb-15 mb-md-0">
              <div className="text-uppercase font-sofia fs-9 fs-md-10 mb-15">
                {filteredShoppingOptions?.header}
              </div>
              <div>
                <ul
                  className={classNames(
                    'm-0 font-roboto fs-7 fs-md-8',
                    styles.twoColumns
                  )}
                >
                  {filteredShoppingOptions?.option_list?.map((option) => (
                    <li key={option.label}>
                      <Link href={option.url}>{option.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <div className="col-12 col-sm">
            {contactHeader && (
              <div className="text-uppercase font-sofia fs-9 fs-md-10 mb-15">
                {contactHeader}
              </div>
            )}
            <div
              className={classNames(
                'font-roboto fs-7 fs-md-8',
                styles.lineHeight
              )}
            >
              {sms && (
                <div>
                  <FontAwesomeIcon
                    icon={faComment}
                    className="pe-7"
                    style={{ width: 25, height: 22 }}
                  />
                  <span>Text: </span>
                  <a aria-label="Send us a text message" href={`sms:${sms}`}>
                    {sms}
                  </a>
                </div>
              )}
              {email && (
                <div>
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="pe-7"
                    style={{ width: 25, height: 22 }}
                  />
                  <span>Email: </span>
                  <a aria-label="Send us an email" href={`mailto:${email}`}>
                    {email}
                  </a>
                </div>
              )}
              {phone && (
                <div>
                  <FontAwesomeIcon
                    icon={faMobileScreen}
                    className="pe-7"
                    style={{ width: 25, height: 22 }}
                  />
                  <span>Phone: </span>
                  <a aria-label="Give us a call" href={`tel:${phone}`}>
                    {phone}
                  </a>
                </div>
              )}
              {changeLocation && (
                <div>
                  <Button
                    data-value="selected-store-link"
                    variant="none"
                    type="button"
                    className="fs-7 fs-md-8 p-0 text-primary"
                    onClick={() =>
                      dispatch(setCurrentModal(MODAL_SELECT_STORE))
                    }
                  >
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      className="pe-7"
                      style={{ width: 25, height: 22 }}
                    />
                    {changeLocation}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoItemsFoundContent;
